'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Send, SmilePlus, Users, MessageCircle, ChevronDown, ExternalLink } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface ChatMessage {
  id: string;
  user: string;
  flag: string;
  text: string;
  time: string;
  reactions: { emoji: string; count: number }[];
  isGoalAlert?: boolean;
  isRemoved?: boolean;
}

function rowToMessage(row: Record<string, unknown>): ChatMessage {
  const createdAt = row.created_at ? new Date(row.created_at as string) : new Date();
  const time = createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  let reactions: { emoji: string; count: number }[] = [];
  try {
    const raw = row.reactions;
    if (Array.isArray(raw)) reactions = raw as { emoji: string; count: number }[];
    else if (typeof raw === 'string') reactions = JSON.parse(raw);
  } catch {}
  return {
    id: row.id as string,
    user: (row.user_name as string) || 'Fan',
    flag: (row.flag as string) || '🌍',
    text: row.text as string,
    time,
    reactions,
    isGoalAlert: (row.is_goal_alert as boolean) || false,
    isRemoved: (row.is_removed as boolean) || false,
  };
}

const quickReactions = ['🔥', '⚽', '😱', '👏', '💪'];

export default function DashboardChatWidget() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [onlineCount] = useState(2847);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();
  const { t } = useLanguage();

  const scrollToBottom = useCallback(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (isAtBottom) scrollToBottom();
  }, [messages, isAtBottom, scrollToBottom]);

  // Load initial messages
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('live_chat_messages')
          .select('*')
          .eq('is_removed', false)
          .order('created_at', { ascending: true })
          .limit(60);
        if (!error) {
          setMessages((data || []).map(rowToMessage));
        }
      } catch {}
      finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('dashboard_chat_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'live_chat_messages' },
        (payload) => {
          const newMsg = rowToMessage(payload.new as Record<string, unknown>);
          if (newMsg.isRemoved) return;
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'live_chat_messages' },
        (payload) => {
          const updated = rowToMessage(payload.new as Record<string, unknown>);
          setMessages((prev) =>
            updated.isRemoved
              ? prev.filter((m) => m.id !== updated.id)
              : prev.map((m) => (m.id === updated.id ? updated : m))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 50);
  };

  const handleSend = async () => {
    if (!inputText.trim()) return;
    const text = inputText.trim();
    setInputText('');
    try {
      await supabase.from('live_chat_messages').insert({
        user_name: 'Fan Noticopa',
        flag: '🌍',
        country: 'Global',
        avatar: 'FN',
        text,
        is_goal_alert: false,
        reactions: [],
      });
    } catch {
      setInputText(text);
    }
  };

  const handleReact = useCallback(
    async (msgId: string, emoji: string) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== msgId) return msg;
          const existing = msg.reactions.find((r) => r.emoji === emoji);
          const updatedReactions = existing
            ? msg.reactions.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1 } : r))
            : [...msg.reactions, { emoji, count: 1 }];
          return { ...msg, reactions: updatedReactions };
        })
      );
      const msg = messages.find((m) => m.id === msgId);
      if (!msg) return;
      const existing = msg.reactions.find((r) => r.emoji === emoji);
      const updatedReactions = existing
        ? msg.reactions.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1 } : r))
        : [...msg.reactions, { emoji, count: 1 }];
      await supabase.from('live_chat_messages').update({ reactions: updatedReactions }).eq('id', msgId);
    },
    [messages]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="flex flex-col rounded-xl overflow-hidden"
      style={{
        background: 'hsl(222 35% 11%)',
        border: '1px solid hsl(222 25% 16%)',
        height: '480px',
        maxHeight: '60vh',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid hsl(222 25% 16%)', background: 'hsl(222 28% 13%)' }}
      >
        <div className="flex items-center gap-2">
          <MessageCircle size={15} className="text-orange-400" />
          <h3 className="text-sm font-semibold text-white">{t.dashboard.liveMatchChat}</h3>
          <span
            className="flex items-center gap-1 text-xs font-bold px-1.5 py-0.5 rounded-full"
            style={{ background: 'hsl(0 84% 60% / 0.12)', color: 'hsl(0 84% 70%)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
            LIVE
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-xs text-green-400">
            <Users size={12} />
            <span className="font-mono tabular-nums font-semibold">{onlineCount.toLocaleString()}</span>
          </div>
          <Link
            href="/live-chat"
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-orange-400 transition-colors"
            title={t.dashboard.openFullChat}
          >
            <ExternalLink size={13} />
          </Link>
        </div>
      </div>

      {/* Messages Feed */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-3 relative"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(222 25% 20%) transparent' }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 rounded-full border-2 border-orange-500 border-t-transparent animate-spin" />
              <span className="text-xs text-slate-500">{t.dashboard.loadingChat}</span>
            </div>
          </div>
        ) : (
          messages.map((msg) => {
            if (msg.isGoalAlert) {
              return (
                <div key={msg.id} className="flex justify-center my-2">
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg max-w-full"
                    style={{ background: 'hsl(25 95% 53% / 0.12)', border: '1px solid hsl(25 95% 53% / 0.25)' }}
                  >
                    <span className="text-base">⚽</span>
                    <p className="text-xs font-bold text-orange-300 truncate">{msg.text}</p>
                  </div>
                </div>
              );
            }
            return (
              <div key={msg.id} className="flex gap-2 group">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5"
                  style={{ background: 'hsl(222 25% 18%)' }}
                >
                  {msg.flag}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs font-semibold text-white truncate">{msg.user}</span>
                    <span className="text-xs text-slate-600 font-mono tabular-nums flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {msg.time}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed break-words">{msg.text}</p>
                  {msg.reactions.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {msg.reactions.slice(0, 3).map((r) => (
                        <button
                          key={`${msg.id}-${r.emoji}`}
                          onClick={() => handleReact(msg.id, r.emoji)}
                          className="flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full transition-all hover:scale-105"
                          style={{ background: 'hsl(222 25% 16%)', border: '1px solid hsl(222 25% 20%)', color: 'hsl(222 15% 65%)' }}
                        >
                          {r.emoji} <span className="font-mono tabular-nums">{r.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom */}
      {!isAtBottom && (
        <div className="flex justify-center pb-1 flex-shrink-0">
          <button
            onClick={scrollToBottom}
            className="flex items-center gap-1 text-xs font-medium px-3 py-1 rounded-full shadow-lg transition-all"
            style={{ background: 'hsl(25 95% 53%)', color: 'white' }}
          >
            <ChevronDown size={12} />
            {t.chat.title}
          </button>
        </div>
      )}

      {/* Quick reactions */}
      <div
        className="flex items-center gap-1.5 px-3 py-2 flex-shrink-0"
        style={{ borderTop: '1px solid hsl(222 25% 16%)' }}
      >
        <SmilePlus size={13} className="text-slate-500 flex-shrink-0" />
        {quickReactions.map((emoji) => (
          <button
            key={`quick-${emoji}`}
            onClick={() => setInputText((prev) => prev + emoji)}
            className="text-sm hover:scale-125 transition-transform flex-shrink-0"
            title={`Add ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Input */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 flex-shrink-0"
        style={{ borderTop: '1px solid hsl(222 25% 16%)', background: 'hsl(222 28% 13%)' }}
      >
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: 'hsl(25 95% 53%)', color: 'white' }}
        >
          FN
        </div>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t.chat.placeholder}
          className="flex-1 bg-transparent text-xs text-white placeholder-slate-600 outline-none min-w-0"
        />
        <button
          onClick={handleSend}
          disabled={!inputText.trim()}
          className="flex items-center justify-center w-7 h-7 rounded-lg transition-all flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: inputText.trim() ? 'hsl(25 95% 53%)' : 'hsl(222 25% 18%)' }}
          aria-label="Send message"
        >
          <Send size={13} className={inputText.trim() ? 'text-white' : 'text-slate-600'} />
        </button>
      </div>
    </div>
  );
}
